import { Fullscreen } from "@pmag/icons";
import { type GQLComponentSubGroupGenericFragment, type GQLHotspotFragment } from "@src/graphql.generated";
import { type FunctionComponent, type MouseEvent, type MouseEventHandler, type ReactEventHandler, useEffect, useState } from "react";

import {
    ExplosionExpandButton,
    ExplosionGraphicBox,
    ExplosionGraphicHotspot,
    ExplosionGraphicHotspots,
    ExplosionGraphicImage,
    ExplosionGraphicImageContainer,
    ExplosionGraphicStickyBox,
} from "./ExplosionGraphic.styles";

export const dataTestId = {
    container: "sparePartsFinder.subgroupPage.explosionGraphic.container",
    hotspots: "sparePartsFinder.subgroupPage.explosionGraphic.hotspots",
    hotspot: "sparePartsFinder.subgroupPage.explosionGraphic.hotspot",
    image: "sparePartsFinder.subgroupPage.explosionGraphic.image",
    expandButton: "sparePartsFinder.subgroupPage.explosionGraphic.button.expand",
};

export interface ExplosionGraphicProps {
    canExpand: boolean;
    componentGroupItem: GQLComponentSubGroupGenericFragment;
    hotspots: GQLHotspotFragment[];
    hotspotsHighlight: GQLHotspotFragment[];
    onMouseEnter: (event: MouseEvent<HTMLDivElement>, hotspot: GQLHotspotFragment) => void;
    onMouseLeave: MouseEventHandler<HTMLDivElement>;
    onToggleOpen: () => void;
    showHotspots: boolean;
}

export const ExplosionGraphic: FunctionComponent<ExplosionGraphicProps> = ({
    onMouseEnter,
    onMouseLeave,
    componentGroupItem,
    hotspots,
    hotspotsHighlight,
    canExpand,
    showHotspots,
    onToggleOpen,
}) => {
    const [imgSrc, setImgSrc] = useState(componentGroupItem.explosionAssetHighResolution);

    useEffect(() => setImgSrc(componentGroupItem.explosionAssetHighResolution), [componentGroupItem]);

    const handleError: ReactEventHandler<HTMLImageElement> = (e) => {
        e.stopPropagation();
        setImgSrc(componentGroupItem.explosionAssetMediumResolution);
    };

    return (
        <ExplosionGraphicStickyBox>
            <ExplosionGraphicBox data-testid={dataTestId.container}>
                {canExpand && (
                    <ExplosionExpandButton variant="ghost" onClick={onToggleOpen} data-testid={dataTestId.expandButton}>
                        <Fullscreen />
                    </ExplosionExpandButton>
                )}

                <ExplosionGraphicImageContainer>
                    {showHotspots && (
                        <ExplosionGraphicHotspots data-testid={dataTestId.hotspots}>
                            {hotspots.map((hotspot) => (
                                <ExplosionGraphicHotspot
                                    left={`${hotspot.startX * 100}%`}
                                    top={`${(1 - hotspot.endY) * 100}%`}
                                    width={`${(hotspot.endX - hotspot.startX) * 100}%`}
                                    height={`${(hotspot.endY - hotspot.startY) * 100}%`}
                                    highlight={hotspotsHighlight.find((hotspotHighlight) => hotspotHighlight.id === hotspot.id) !== undefined}
                                    onMouseEnter={(event: MouseEvent<HTMLDivElement>) => onMouseEnter(event, hotspot)}
                                    onMouseLeave={onMouseLeave}
                                    key={hotspot.id}
                                    data-testid={dataTestId.hotspot}
                                />
                            ))}
                        </ExplosionGraphicHotspots>
                    )}

                    {imgSrc && <ExplosionGraphicImage src={imgSrc} data-testid={dataTestId.image} onError={handleError} />}
                </ExplosionGraphicImageContainer>
            </ExplosionGraphicBox>
        </ExplosionGraphicStickyBox>
    );
};
